import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Typography,
  TextField,
  Box,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { fetchDepartments } from '../services/api';
import { formatPrevSubmission } from '../utils/formatPrevSubmission';
import IconOnlyChip from './IconOnlyChip';

const EditDepartmentAdminDialog = ({ open, onClose, department: deptProp, onResubmit }) => {
  const department = deptProp ?? {};
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [departments, setDepartments] = useState([]);
  const [duplicate, setDuplicate] = useState(false);
  const [nameDirty, setNameDirty] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const toggleEditing = () => {
    setEditing((prev) => {
      if (prev) {
        setName('');
        setDuplicate(false);
        setNameDirty(false);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (open) {
      setEditing(false);
      setName('');
      setDuplicate(false);
      setNameDirty(false);
      setConfirmed(false);
      const instId = deptProp?.institution?.id;
      if (instId) {
        fetchDepartments(instId)
          .then((res) => {
            setDepartments(res || []);
          })
          .catch(() => setDepartments([]));
      } else {
        setDepartments([]);
      }
    }
  }, [open, deptProp]);

  useEffect(() => {
    if (!editing) {
      setDuplicate(false);
      return;
    }
    const lower = name.trim().toLowerCase();
    const dup =
      lower &&
      departments.some(
        (d) => d.id !== department.id && d.name.toLowerCase() === lower
      );
    setDuplicate(dup);
  }, [name, departments, editing, department.id]);

  const changed =
    editing &&
    nameDirty &&
    name.trim() &&
    name.trim() !== department.name &&
    !duplicate;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!deptProp) return;
    const payload = {};
    if (editing && nameDirty && name.trim() !== department.name && !duplicate) {
      payload.name = name.trim();
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(department.id, payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Department</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department Name
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{department.name || '—'}</Typography>
            <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggleEditing} />
          </Box>
          {editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameDirty(true);
                }}
                placeholder="New Department Name"
                error={duplicate}
                helperText={duplicate ? 'Department with this name already exists.' : ''}
              />
              <IconOnlyChip icon={<CancelIcon fontSize="small" />} onClick={toggleEditing} />
            </Box>
          )}
        </Box>
        {department.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{department.rejection_reason}</Typography>
          </Box>
        )}
        {department.rejection_reason && department.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{formatPrevSubmission(department.previous_submission)}</Typography>
          </Box>
        )}
      </DialogContent>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
        }
        label="I confirm that by submitting this edit I'm transferring the ownership of the submission to myself."
        sx={{ mx: 3 }}
      />
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={!confirmed || !changed}
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditDepartmentAdminDialog;
